import { AssetBuilderFiltersType } from "#src/batteries-included-components/FilterAreas/AssetBuilderFilterAreas/AssetBuilderFilterAreas";
import { AssetTemplateRadioInput } from "#src/batteries-included-components/Forms/Fields/AssetTemplateRadioInput";
import { useListTemplatedConfigurations } from "#src/components/hooks/adapters/useTemplatedConfigurations";
import useLocalization from "#src/hooks/useLocalization";
import { useMultiStepFormContext } from "#src/hooks/useMultiStepForm";
import { useStickyState } from "#src/hooks/useStickyState";
import {
  DropdownInput,
  Form,
  Panel,
  type StorageKeys,
  TextInput,
  useForm,
} from "@validereinc/common-components";
import {
  AssetType,
  AssetTypeType,
  Resources,
  TemplatedConfigurationCompanyStatus,
  TemplatedConfigurationSchema,
} from "@validereinc/domain";
import classNames from "classnames/bind";
import React, { useEffect } from "react";
import styles from "./FacilityForm.module.scss";
const cx = classNames.bind(styles);

const STEP_NUMBER = 1;

export const FacilityOverviewFormStep = ({
  filterConfigStorageKey,
  shouldUseAssetBuilder,
}: {
  shouldUseAssetBuilder?: boolean;
} & Pick<StorageKeys, "filterConfigStorageKey">) => {
  const { currentStep, updateStep } = useMultiStepFormContext();
  const [filters] = useStickyState<AssetBuilderFiltersType>(
    {} as AssetBuilderFiltersType,
    filterConfigStorageKey
  );

  const { displayName, sortDirection } = filters;

  const { localize } = useLocalization();

  const form = useForm<{
    asset_type: AssetTypeType;
    name?: string;
    template_name?: string;
  }>({
    defaultValues: {
      asset_type: AssetType.FACILITY,
      name: "",
      template_name: "",
    },
  });

  const formValues = form.watch();

  const isTemplateQueryEnabled = shouldUseAssetBuilder;

  const availableTemplatesQuery = useListTemplatedConfigurations(
    {
      filters: {
        primary_resource_type: Resources.FACILITY,
        display_name: displayName,
        // @ts-expect-error TODO: Typing on filter
        templated_configuration_company_status: {
          $exact: TemplatedConfigurationCompanyStatus.ACTIVE,
        },
      },
      sortDirection,
      sortBy: TemplatedConfigurationSchema.keyof().Enum.display_name,
    },
    { enabled: isTemplateQueryEnabled }
  );

  const availableTemplatesData = availableTemplatesQuery.data?.data ?? [];

  const availableTemplatesOptions = availableTemplatesData.map((template) => {
    return {
      title: template.display_name,
      description: template.description ?? "",
      value: template.name,
    };
  });

  useEffect(() => {
    updateStep(STEP_NUMBER, {
      getValues: () => form.getValues(),
      getFormState: () => ({
        isSubmitting: false,
        isValid: !shouldUseAssetBuilder
          ? !!formValues.name
          : !!formValues.template_name,
      }),
    });
  }, [JSON.stringify(formValues)]);

  if (currentStep !== STEP_NUMBER) return null;

  return (
    <Panel className={cx("panel")}>
      <Form {...form}>
        <h4 className={cx("section-title")}>Overview</h4>

        {!shouldUseAssetBuilder ? (
          <p className={cx("section-description")}>
            {`Quick Create will create only the single ${localize(AssetType.FACILITY)} whose details are specified below.`}
          </p>
        ) : null}
        <div className={cx("basic-info-container")}>
          {shouldUseAssetBuilder ? (
            <DropdownInput
              label="Asset Type"
              name="asset_type"
              options={[
                { label: localize(AssetType.FACILITY), value: "facility" },
              ]}
              labelKey="label"
              valueKey="value"
              isRequired
              isDisabled
              isFluid
            />
          ) : (
            <TextInput
              label="Name"
              name="name"
              placeholder="Enter Name..."
              isFluid
              isRequired
            />
          )}

          {shouldUseAssetBuilder ? (
            <>
              <AssetTemplateRadioInput
                filterConfigStorageKey={filterConfigStorageKey}
                assetType={AssetType.FACILITY}
                isTemplateQueryDisabled={!isTemplateQueryEnabled}
                availableTemplatesQueryIsLoading={
                  availableTemplatesQuery.isLoading
                }
                availableTemplatesOptions={availableTemplatesOptions}
                isOptionSelected={Boolean(formValues.template_name)}
              />
            </>
          ) : null}
        </div>
      </Form>
    </Panel>
  );
};
